/**
 * HighlightedBlock — bloc de code coloré avec titre + line numbers + copy.
 *
 * @remarks
 * Partagé par le rendu markdown (`CodeBlock`) et `candidate-results` : vit dans
 * `shared` depuis SAAS-917 (un composant partagé importait une feature).
 * Le legacy avait collapse + theme switcher
 * — ces features arriveront avec la migration `MarkdownContent` complète.
 */

import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Check, Copy } from 'lucide-react';
import { Icon } from '@/shared/ui';

interface HighlightedBlockProps {
  language: string;
  codeText: string;
  title?: string;
  startingLineNumber?: number;
  blockId?: string;
  /**
   * `'always'` (défaut) : numéros toujours affichés — un extrait de code cité
   * à une ligne précise en a besoin. `'preference'` : ils suivent le réglage
   * « Show line numbers » (attribut `data-code-line-numbers` sur `<html>`).
   */
  lineNumbers?: 'always' | 'preference';
}

export function HighlightedBlock({
  language,
  codeText,
  title,
  startingLineNumber = 1,
  blockId,
  lineNumbers = 'always',
}: HighlightedBlockProps) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeText);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div
      data-block-id={blockId}
      data-line-numbers={lineNumbers}
      className="border-border my-2 overflow-hidden rounded-md border bg-[#282c34]"
    >
      <div className="flex items-center justify-between gap-2 border-b border-black/30 bg-black/20 px-2 py-1 text-xs text-white/70">
        <span className="font-mono">{title ?? language}</span>
        <button
          type="button"
          onClick={() => {
            void onCopy();
          }}
          aria-label="Copier"
          className="rounded-sm p-1 text-white/60 hover:bg-white/10 hover:text-white"
        >
          {copied ? <Icon icon={Check} size="sm" /> : <Icon icon={Copy} size="sm" />}
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={oneDark}
        showLineNumbers
        startingLineNumber={startingLineNumber}
        customStyle={{
          margin: 0,
          fontSize: 'var(--font-size-sm)',
          padding: 'var(--spacing-sm)',
        }}
      >
        {codeText}
      </SyntaxHighlighter>
    </div>
  );
}
